// src/pages/CheckoutPage.jsx
import React, { useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { useLanguage } from '../hooks/useLanguage';
import { UserContext } from '../context/UserContext';
import { ConfigContext } from '../context/ConfigContext';
import { NotificationContext } from '../context/NotificationContext';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';
import { convertCreditsToTargetCurrency, formatCurrency } from '../utils/priceCalculator';
import { getSessionId } from '../components/topbar/SessionTracker'; // Import the session ID utility
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const { cart, clearCart } = useContext(CartContext);
  const { user, updateUser } = useContext(UserContext);
  const { config } = useContext(ConfigContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const totalCostInCredits = useMemo(() => {
    return cart.items.reduce((total, item) => total + item.costInCredits, 0);
  }, [cart.items]);

  const availablePoints = useMemo(() => {
    return Math.max(0, (user.creditPoints || 0) - (user.pointsOnHold || 0));
  }, [user.creditPoints, user.pointsOnHold]);

  const creditsToUse = useMemo(() => {
    return Math.min(availablePoints, totalCostInCredits);
  }, [availablePoints, totalCostInCredits]);

  const creditsToPay = useMemo(() => {
    return Math.max(0, totalCostInCredits - availablePoints);
  }, [totalCostInCredits, availablePoints]);

  const { totalCostInCurrency, creditsValueInCurrency, finalAmountInCurrency, rates, currency } = useMemo(() => {
    if (!config || !user.isLoggedIn) {
      return { totalCostInCurrency: '...', creditsValueInCurrency: '...', finalAmountInCurrency: '...', rates: null, currency: null };
    }
    const localRates = {
      usdToIlsRate: config.usdToIlsRate,
      eurToIlsRate: config.eurToIlsRate,
      creditPointValueILS: config.creditPointValueILS,
    };
    const localCurrency = user.preferences.currency;

    const totalCost = formatCurrency(convertCreditsToTargetCurrency(totalCostInCredits, localCurrency, localRates), localCurrency);
    const creditsValue = formatCurrency(convertCreditsToTargetCurrency(creditsToUse, localCurrency, localRates), localCurrency);
    const finalAmount = formatCurrency(convertCreditsToTargetCurrency(creditsToPay, localCurrency, localRates), localCurrency);

    return {
      totalCostInCurrency: totalCost,
      creditsValueInCurrency: creditsValue,
      finalAmountInCurrency: finalAmount,
      rates: localRates,
      currency: localCurrency,
    };
  }, [totalCostInCredits, creditsToUse, creditsToPay, user, config]);

  const handlePlaceOrder = async () => {
    setIsLoading(true);
    //    addNotification('Placing your order...', 'info');

    try {
      const response = await apiFetch(
        `${BASE_URL}/api/payments/cart-checkout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: getSessionId(), // Send the session ID to the backend
          }),
        },
        user.token
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('checkoutPage.placeOrder.failed'));
      }

      // Update user's credit points from the response
      if (result.user) {
        updateUser({
          creditPoints: result.user.creditPoints,
          pointsOnHold: result.user.pointsOnHold,
        });
      }

      if (result.paymentNeeded) {
        //        addNotification('Order placed! Proceeding to payment.', 'success');
        navigate(`/${lang}/select-payment?orderId=${result.orderId}`);
      } else {
        addNotification(t('checkoutPage.placeOrder.successWithCredits'), 'success');
        // The order was paid entirely with credits, so we can now clear the cart.
        clearCart();
        // The backend will trigger fulfillment, the frontend can just go to the orders page.
        navigate(`/${lang}/my-orders`);
      }
    } catch (error) {
      addNotification(t('checkoutPage.placeOrder.error', { message: error.message }), 'error');
      setIsLoading(false);
    }
    // No need to setIsLoading(false) on success because we navigate away.
  };

  if (cart.items.length === 0 && !isLoading) {
    return (
      <div className="checkout-page-container">
        <h1>{t('checkoutPage.title')}</h1>
        <p>{t('checkoutPage.emptyCart.message')}</p>
      </div>
    );
  }

  return (
    <div className="checkout-page-container">
      {isLoading && (
        <div className="loading-overlay">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>{t('checkoutPage.loading.processing')}</p>
        </div>
      )}
      <h1>{t('checkoutPage.title')}</h1>
      <div className="checkout-content">
        <div className="checkout-items-list">
          <h3>{t('checkoutPage.orderSummary.title')}</h3>
          {cart.items.map((item) => {
            const itemCostInCurrency = rates && currency ? formatCurrency(convertCreditsToTargetCurrency(item.costInCredits, currency, rates), currency) : '...';
            return (
              <div key={item._id} className="checkout-item">
                <span className="item-name">{item.name}</span>
                <span className="item-cost">{itemCostInCurrency}</span>
              </div>
            );
          })}
        </div>
        <div className="checkout-summary">
          <h3>{t('checkoutPage.paymentDetails.title')}</h3>
          <div className="summary-line">
            <span>{t('checkoutPage.paymentDetails.totalCost')}</span>
            <span>{totalCostInCurrency}</span>
          </div>
          <div className="summary-line">
            <span>{t('checkoutPage.paymentDetails.creditsApplied')}</span>
            <span>- {creditsValueInCurrency}</span>
          </div>
          <hr />
          <div className="summary-line total">
            <span>{t('checkoutPage.paymentDetails.finalAmount')}</span>
            <span>{finalAmountInCurrency}</span>
          </div>
          <button className="btn btn-primary place-order-btn" onClick={handlePlaceOrder} disabled={isLoading}>
            {isLoading ? t('checkoutPage.placeOrder.processing') : t('checkoutPage.placeOrder.button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
